import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../App';
import TabNavigation from '../../components/TabNavigation';

const AuditRecords = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('All');
  const [filteredRecords, setFilteredRecords] = useState([]);

  const statusOptions = ['All', 'Draft', 'Submitted', 'Reviewed', 'Approved', 'CAPA Outstanding', 'Closed'];

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (filterStatus === 'All') {
      setFilteredRecords(records);
    } else {
      setFilteredRecords(records.filter(r => r.workflow_status === filterStatus));
    }
  }, [filterStatus, records]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [recordsRes, schedulesRes] = await Promise.all([
        axios.get(`${API}/audit-records`, { headers }),
        axios.get(`${API}/audit-schedules`, { headers })
      ]);
      
      setRecords(recordsRes.data);
      setSchedules(schedulesRes.data);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Draft': 'bg-gray-100 text-gray-700',
      'Submitted': 'bg-blue-100 text-blue-700',
      'Reviewed': 'bg-purple-100 text-purple-700',
      'Approved': 'bg-green-100 text-green-700',
      'CAPA Outstanding': 'bg-yellow-100 text-yellow-700',
      'Closed': 'bg-teal-100 text-teal-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const getComplianceColor = (score) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 70) return 'text-yellow-600';
    return 'text-red-600';
  };

  const startAudit = (schedule) => {
    navigate('/audits/conduct', { state: { schedule } });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="p-6">
        <div className="mb-6">
          <button
            onClick={() => navigate('/audits')}
            className="text-blue-600 hover:underline mb-4 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Audits Dashboard
          </button>
          <h1 className="text-3xl font-bold text-gray-800">📝 Audit Records</h1>
          <p className="text-gray-600 mt-1">{filteredRecords.length} audit(s) found</p>
        </div>

        {/* Filter Buttons */}
        <div className="bg-white rounded-xl shadow-md p-4 mb-6">
          <div className="flex flex-wrap gap-2">
            {statusOptions.map(status => (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                  filterStatus === status
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {status}
              </button>
            ))}
          </div>
        </div>

        {/* Scheduled Audits - Pending Completion */}
        {schedules.filter(s => s.status === 'Scheduled').length > 0 && (
          <div className="mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">🔔 Scheduled Audits - Start Audit</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {schedules.filter(s => s.status === 'Scheduled').map(schedule => (
                <div key={schedule.id} className="bg-white rounded-xl shadow-md p-5 border-l-4 border-purple-500">
                  <h3 className="font-bold text-gray-800 mb-2">{schedule.audit_title}</h3>
                  <p className="text-sm text-gray-600 mb-1">Auditor: {schedule.auditor_name}</p>
                  <p className="text-sm text-gray-600 mb-1">Template: {schedule.template_name}</p>
                  <p className="text-sm text-gray-500 mb-3">
                    {new Date(schedule.scheduled_date).toLocaleDateString()} at {new Date(schedule.scheduled_date).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                  </p>
                  {schedule.location && (
                    <p className="text-xs text-gray-500 mb-3">📍 {schedule.location}</p>
                  )}
                  <button
                    onClick={() => startAudit(schedule)}
                    className="w-full bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center justify-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Start Audit
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Completed Audits Records */}
        <div>
          <h2 className="text-xl font-bold text-gray-800 mb-4">📊 Audit Records</h2>
          {filteredRecords.length > 0 ? (
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Audit Title
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Auditor
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Compliance
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Findings
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredRecords.map(record => (
                    <tr key={record.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="text-sm font-semibold text-gray-800">{record.audit_title}</div>
                        {record.location && (
                          <div className="text-xs text-gray-500">📍 {record.location}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{record.audit_type}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {new Date(record.audit_date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{record.auditor_name}</td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${getStatusColor(record.workflow_status)}`}>
                          {record.workflow_status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {record.compliance_score !== null ? (
                          <span className={`text-sm font-bold ${getComplianceColor(record.compliance_score)}`}>
                            {record.compliance_score.toFixed(1)}%
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-xs">
                          {record.critical_findings > 0 && (
                            <span className="bg-red-100 text-red-700 px-2 py-1 rounded mr-1">
                              {record.critical_findings} Critical
                            </span>
                          )}
                          {record.major_findings > 0 && (
                            <span className="bg-orange-100 text-orange-700 px-2 py-1 rounded mr-1">
                              {record.major_findings} Major
                            </span>
                          )}
                          {record.failed_checks === 0 && (
                            <span className="text-green-600">No Issues</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => navigate(`/audits/detail/${record.id}`)}
                          className="text-blue-600 hover:underline text-sm"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-md p-12 text-center">
              <p className="text-gray-600">No audit records found for the selected filter.</p>
            </div>
          )}
        </div>
      </div>
    </div>
      </div>
  );
};

export default AuditRecords;
